/**
 * iter86 — Sync Overrides admin tab.
 *
 * When a supplier feed (SanMar / S&S) omits or ships a broken image for
 * a specific colour+side, the operator uploads a corrective image here.
 *
 * How the fix persists across environments:
 *   1. Upload file (or paste URL) here.
 *   2. Backend stores the metadata in `product_image_overrides` and
 *      (for file uploads) the raw bytes inline in Mongo. It also
 *      applies the override to the product immediately.
 *   3. On every backend restart, `apply_image_overrides()` re-applies
 *      all overrides — so a fresh production DB self-heals on deploy.
 *
 * Because Mongo is per-environment, an override added on Preview does
 * NOT automatically appear on Production. You must re-add it on prod
 * once (or promote it to a "seed" entry in
 * `backend/services/image_overrides.py` for auto-application on any
 * environment).
 */
import { useCallback, useEffect, useState } from "react";
import { RefreshCw, Trash2, Upload, Link as LinkIcon, Image as ImageIcon, AlertCircle, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import {
  adminListImageOverrides,
  adminCreateImageOverride,
  adminDeleteImageOverride,
  adminReapplyImageOverrides,
  publicGetProduct,
} from "../../lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function OverridesTab({ pw }) {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ manual: 0, seed: 0 });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const d = await adminListImageOverrides(pw);
      setRows(d.overrides || []);
      setCounts(d.counts || { manual: 0, seed: 0 });
    } catch (e) {
      toast.error(`Failed to load overrides: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, [pw]);

  useEffect(() => { refresh(); }, [refresh]);

  const onDelete = async (ovId) => {
    if (!window.confirm("Remove this override? The image will be cleared until the next sync (or a new override).")) return;
    try {
      await adminDeleteImageOverride(pw, ovId);
      toast.success("Override removed");
      refresh();
    } catch (e) {
      toast.error(`Delete failed: ${e.response?.data?.detail || e.message}`);
    }
  };

  const onReapply = async () => {
    setBusy(true);
    try {
      const r = await adminReapplyImageOverrides(pw);
      const s = r.stats || {};
      toast.success(
        `Re-apply done — ${s.applied || 0} written, ${s.already_ok || 0} already ok` +
        (s.missing_product ? `, ${s.missing_product} missing product${s.missing_product > 1 ? "s" : ""}` : "")
      );
      refresh();
    } catch (e) {
      toast.error(`Reapply failed: ${e.response?.data?.detail || e.message}`);
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-6" data-testid="overrides-tab">
      <div className="flex justify-between items-start flex-wrap gap-4">
        <div>
          <h2 className="font-display text-2xl uppercase tracking-tight flex items-center gap-2">
            <ImageIcon size={22} className="text-[var(--tc-red)]" /> Sync Overrides
          </h2>
          <p className="font-body text-sm text-zinc-400 mt-1 max-w-2xl">
            Fix broken or missing product images without waiting for a supplier re-sync.
            Overrides apply immediately and re-apply on every backend restart, so they survive
            SanMar / S&amp;S refreshes. Preview and production have separate databases — you&apos;ll
            need to add manual overrides on each environment (or promote them to seed in
            <code className="mx-1 px-1 py-0.5 bg-[#111] font-mono text-[10px]">backend/services/image_overrides.py</code>
            for auto-application everywhere).
          </p>
        </div>
        <button
          onClick={onReapply}
          disabled={busy}
          data-testid="overrides-reapply"
          className="border border-[#222] hover:border-white px-3 py-2 font-mono text-[10px] tracking-widest inline-flex items-center gap-2 disabled:opacity-50"
        >
          <RefreshCw size={12} className={busy ? "animate-spin" : ""} /> RE-APPLY ALL
        </button>
      </div>

      <NewOverrideForm pw={pw} onCreated={refresh} />

      <div>
        <div className="font-mono text-[11px] tracking-widest text-zinc-400 mb-3">
          / ACTIVE OVERRIDES · {counts.manual} MANUAL · {counts.seed} SEED
        </div>
        {loading ? (
          <div className="text-zinc-500 font-mono text-xs">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="border border-[#222] p-8 text-center text-zinc-500 font-mono text-xs">
            No overrides yet. Add one above to fix a missing product image.
          </div>
        ) : (
          <div className="space-y-2">
            {rows.map((r, i) => (
              <OverrideRow key={r.id || `seed-${i}`} row={r} onDelete={r.id ? () => onDelete(r.id) : null} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function OverrideRow({ row, onDelete }) {
  const isSeed = row.source === "seed";
  // Blob-served overrides use a relative URL — prefix with the backend
  // origin so <img> can resolve it (browser default host would be the
  // preview frontend which doesn't serve /api/overrides/...).
  const displayUrl = row.image_url && row.image_url.startsWith("/api/")
    ? `${BACKEND_URL}${row.image_url}`
    : row.image_url;
  return (
    <div
      className="border border-[#222] p-3 flex flex-wrap items-center gap-4"
      data-testid={`override-row-${row.slug}-${row.color}-${row.side}`}
    >
      <div className="w-20 h-20 bg-[#0d0d0d] border border-[#333] flex items-center justify-center overflow-hidden shrink-0">
        <img
          src={displayUrl}
          alt=""
          className="w-full h-full object-contain"
          onError={(e) => { e.currentTarget.style.display = "none"; }}
        />
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-ui uppercase tracking-wider text-sm truncate">{row.slug}</div>
        <div className="font-mono text-[10px] tracking-widest text-zinc-400 mt-0.5">
          <span className="text-white">{row.color}</span>
          <span className="mx-2 text-zinc-600">·</span>
          {row.side.toUpperCase()}
          <span className={`ml-2 px-1.5 py-0.5 border ${
            isSeed ? "border-blue-800 text-blue-300 bg-blue-950/30" : "border-emerald-800 text-emerald-300 bg-emerald-950/30"
          }`}>
            {isSeed ? "SEED (auto-applies on every env)" : "MANUAL (this env only)"}
          </span>
        </div>
        <div className="font-mono text-[9px] text-zinc-500 truncate mt-1">{row.image_url}</div>
      </div>
      {onDelete ? (
        <button
          onClick={onDelete}
          data-testid={`override-delete-${row.id}`}
          className="border border-red-900 hover:border-red-500 text-red-400 hover:text-red-200 px-3 py-2 font-mono text-[10px] tracking-widest inline-flex items-center gap-2"
        >
          <Trash2 size={12} /> REMOVE
        </button>
      ) : (
        <span className="font-mono text-[10px] text-zinc-500 tracking-widest px-3 py-2 border border-dashed border-[#333]">
          IN CODE
        </span>
      )}
    </div>
  );
}

function NewOverrideForm({ pw, onCreated }) {
  const [slug, setSlug] = useState("");
  const [product, setProduct] = useState(null);
  const [color, setColor] = useState("");
  const [side, setSide] = useState("back");
  const [mode, setMode] = useState("upload"); // upload | url
  const [file, setFile] = useState(null);
  const [imageUrl, setImageUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [lookupBusy, setLookupBusy] = useState(false);

  // Look up product colours when slug changes (debounced).
  useEffect(() => {
    if (!slug.trim()) { setProduct(null); return; }
    const t = setTimeout(async () => {
      setLookupBusy(true);
      try {
        const p = await publicGetProduct(slug.trim());
        setProduct(p);
        if (p?.colors?.length && !p.colors.includes(color)) setColor(p.colors[0]);
      } catch {
        setProduct({ __missing: true });
      } finally { setLookupBusy(false); }
    }, 400);
    return () => clearTimeout(t);
  }, [slug]);

  const canSubmit = slug.trim() && color && side && (
    (mode === "upload" && file) ||
    (mode === "url" && imageUrl.trim())
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit || busy) return;
    setBusy(true);
    try {
      await adminCreateImageOverride(pw, {
        slug: slug.trim(), color, side,
        imageFile: mode === "upload" ? file : null,
        imageUrl: mode === "url" ? imageUrl.trim() : null,
      });
      toast.success(`Override saved for ${slug} / ${color} / ${side}`);
      setFile(null);
      setImageUrl("");
      onCreated?.();
    } catch (err) {
      toast.error(`Save failed: ${err.response?.data?.detail || err.message}`);
    } finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="border border-[#222] p-4 space-y-4" data-testid="overrides-new-form">
      <div className="font-mono text-[10px] tracking-widest text-zinc-500">/ ADD OVERRIDE</div>

      <div className="grid sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="font-mono text-[9px] tracking-widest text-zinc-500 uppercase">Product Slug</span>
          <input
            type="text"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            placeholder="eurospun-atc8015"
            data-testid="overrides-slug"
            className="w-full bg-black border border-[#222] focus:border-[var(--tc-red)] px-3 py-2 mt-1 font-mono text-xs outline-none"
          />
          {slug && (
            <div className="mt-1.5 font-mono text-[9px] tracking-widest">
              {lookupBusy ? (
                <span className="text-zinc-500">LOOKING UP…</span>
              ) : product?.__missing ? (
                <span className="text-red-400 flex items-center gap-1"><AlertCircle size={10} /> PRODUCT NOT FOUND</span>
              ) : product?.name ? (
                <span className="text-emerald-400 flex items-center gap-1">
                  <CheckCircle2 size={10} /> {product.name.slice(0, 60)}
                </span>
              ) : null}
            </div>
          )}
        </label>

        <label className="block">
          <span className="font-mono text-[9px] tracking-widest text-zinc-500 uppercase">Colour</span>
          <select
            value={color}
            onChange={(e) => setColor(e.target.value)}
            data-testid="overrides-color"
            className="w-full bg-black border border-[#222] focus:border-[var(--tc-red)] px-3 py-2 mt-1 font-mono text-xs outline-none"
            disabled={!product || product.__missing}
          >
            {(!product || product.__missing) && <option value="">— select a product first —</option>}
            {product?.colors?.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
      </div>

      <div className="flex flex-wrap items-center gap-4">
        <div className="flex gap-1">
          {["front", "back"].map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setSide(s)}
              data-testid={`overrides-side-${s}`}
              className={`px-3 py-1.5 border font-mono text-[10px] tracking-widest ${
                side === s ? "bg-[var(--tc-red)] border-[var(--tc-red)] text-white" : "border-[#222] text-zinc-400 hover:text-white"
              }`}
            >{s.toUpperCase()}</button>
          ))}
        </div>
        <div className="flex gap-1 ml-auto">
          {[["upload", Upload, "UPLOAD FILE"], ["url", LinkIcon, "PASTE URL"]].map(([m, Icon, l]) => (
            <button
              key={m}
              type="button"
              onClick={() => setMode(m)}
              data-testid={`overrides-mode-${m}`}
              className={`px-3 py-1.5 border font-mono text-[10px] tracking-widest inline-flex items-center gap-1.5 ${
                mode === m ? "bg-white text-black border-white" : "border-[#222] text-zinc-400 hover:text-white"
              }`}
            ><Icon size={11} /> {l}</button>
          ))}
        </div>
      </div>

      {mode === "upload" ? (
        <label
          className="block border border-dashed border-[#333] hover:border-[var(--tc-red)] p-6 text-center cursor-pointer transition-colors"
          data-testid="overrides-upload-drop"
        >
          <Upload size={20} className="mx-auto text-zinc-500 mb-2" />
          <div className="font-mono text-[11px] tracking-widest text-zinc-400">
            {file ? file.name : "CLICK TO SELECT AN IMAGE"}
          </div>
          <div className="font-mono text-[9px] tracking-widest text-zinc-600 mt-1">
            JPEG / PNG / WEBP · max 4MB · stored inline in Mongo (survives deploys)
          </div>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
            className="hidden"
          />
        </label>
      ) : (
        <label className="block">
          <span className="font-mono text-[9px] tracking-widest text-zinc-500 uppercase">Image URL</span>
          <input
            type="url"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            placeholder="https://customer-assets.emergentagent.com/…"
            data-testid="overrides-url"
            className="w-full bg-black border border-[#222] focus:border-[var(--tc-red)] px-3 py-2 mt-1 font-mono text-xs outline-none"
          />
        </label>
      )}

      <button
        type="submit"
        disabled={!canSubmit || busy}
        data-testid="overrides-submit"
        className="w-full sm:w-auto border border-[var(--tc-red)] bg-[var(--tc-red)] hover:bg-red-700 text-white font-mono text-[11px] tracking-widest px-6 py-2.5 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
      >
        {busy ? <RefreshCw size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
        {busy ? "SAVING…" : "SAVE OVERRIDE & APPLY"}
      </button>
    </form>
  );
}
